import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import { useLayoutEffect, useRef } from 'react';
import { px2DPIpx } from '../utils';
import { PickerColumnItem, PickerColumnValue } from './types';
import { bound, rubberbandIfOutOfBounds } from './utils';

const classPrefix = `v-picker`;

interface ColumnProps {
  pickerDataList: PickerColumnItem[];
  value?: PickerColumnValue;
  onChange?: (pickerColumnItem: PickerColumnItem) => void;
  itemHeight: number;
}

export const Column = (props: ColumnProps) => {
  const itemHeight = parseFloat(px2DPIpx(props.itemHeight));
  const { value, onChange, pickerDataList } = props;
  const [{ y }, api] = useSpring(() => ({
    from: { y: 0 },
    config: {
      tension: 400,
      mass: 0.4,
    },
  }));

  const draggingRef = useRef(false);

  useLayoutEffect(() => {
    if (draggingRef.current) return;
    const targetIndex = pickerDataList.findIndex((item) => item.value === value);
    if (targetIndex < 0) return;
    const finalPosition = targetIndex * -itemHeight;
    // api.start({ y: finalPosition, immediate: y.idle });
    api.start({ y: finalPosition });
  }, [value, pickerDataList, itemHeight, api]);

  function scrollSelect(index: number) {
    const finalPosition = index * -itemHeight;
    api.start({ y: finalPosition });
    onChange?.(pickerDataList[Math.abs(index)]);
  }

  const bind = useDrag(
    (state) => {
      draggingRef.current = true;
      const min = -((pickerDataList.length - 1) * itemHeight);
      const max = 0;
      if (state.last) {
        draggingRef.current = false;
        const position = state.offset[1] + state.velocity[1] * state.direction[1] * 50;
        const targetIndex = -Math.round(bound(position, min, max) / itemHeight);
        scrollSelect(targetIndex);
      } else {
        const position = state.offset[1];
        api.start({
          y: rubberbandIfOutOfBounds(position, min, max, itemHeight * 50, 0.2),
        });
      }
    },
    {
      axis: 'y',
      from: () => [0, y.get()],
      filterTaps: true,
      pointer: { touch: true },
    },
  );

  return (
    <div className={`${classPrefix}-column`} {...bind()}>
      <animated.div style={{ y }} className={`${classPrefix}-column-wheel`}>
        {pickerDataList.map((item, index) => {
          function handleClick() {
            draggingRef.current = false;
            scrollSelect(index);
          }
          return (
            <div key={item.value} className={`${classPrefix}-column-item`} onClick={handleClick}>
              <div className={`${classPrefix}-column-item-label`}>{item.label}</div>
            </div>
          );
        })}
      </animated.div>
    </div>
  );
};
